import { useRef, useState, useEffect, useMemo } from "react";
import { updateActiveTask, Util } from "../../util/Util"
import { canEditSalePrice, canSellBelowMinPrice, catalogMinSellPrice } from "../../util/salePrice"
import { mapTasksWithActivation, refreshEmptyActiveTaskDate } from "../../util/taskActivation";
import { loadPendingTasks, persistPendingTasks, revivePendingItems } from "../../util/pendingTaskStorage";
import { usePendingWorkSessions } from "../../hooks/usePendingWorkSessions";
import { flushItemTableItems } from "../../util/itemTableFlush";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Item } from "../../util/Item";
import TaskPane from "../../components/TaskPane";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { useSale } from "./SaleContext";
import { SaleTask } from './SaleContext';
import { useLocation, useNavigate } from "react-router-dom";
import { Badge, Box, Fab, IconButton, SpeedDial, SpeedDialAction, SpeedDialIcon, Tooltip } from "@mui/material";
import '../../styles/pos.css'
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckIcon from '@mui/icons-material/Check';
import { printReceiptPdf, setupQzTray } from "../../util/qzTray";
import { LocalStorageKeys } from "../../util/constants";
import { expandPaymentEntries } from "../voucher/voucherTender";
import { useAppSocket } from "../../providers/AppSocketContext";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ArrowBack, Close, PowerOff, Print, RemoveCircleOutline } from "@mui/icons-material";
import PosAliasEditCell from "../../components/PosAliasEditCell";
import ShutdownScreen from "../../components/ShutdownScreen";
import { v4 } from "uuid";
import PosClassicLayout from "./PosClassicLayout";
import PosVisualLayout from "./PosVisualLayout";
import PosSplitLayout from "./PosSplitLayout";
import {
	PosLayoutMode,
	POS_LAYOUT_CHANGE_EVENT,
	readStoredPosLayoutMode,
	writeStoredPosLayoutMode,
} from "./posLayouts";
import { POS_DISCONNECT_EVENT } from "../../layouts/TopBar";
import { Z_INDEX } from "../../styles/zIndex";
import { useActiveTaskStatusTotal } from "../../hooks/useActiveTaskStatusTotal";


import { addMoney, compareMoney } from "../../util/quantity";
import { contactToPayload } from "../client/contactBlock";
import { useEphemeralEditTask } from "../../hooks/useEphemeralEditTask";
import { canChangeLineQty, canPrintReceipt, canRemoveLine } from '../../util/access';
const PointOfSale = () => {
	const [taskHash, setTaskHash] = useState<string | null>(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [tasksOpen, setTasksOpen] = useState(false);
	const [renderChildren, setRenderChildren] = useState(false);
	const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
	const [validateDetail, setValidateDetail] = useState(false);
	const [sdOpen, setSDOpen] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [disconnecting, setDisconnecting] = useState(false);
	const [printConfirmOpen, setPrintConfirmOpen] = useState(false);
	const [layoutMode, setLayoutMode] = useState<PosLayoutMode>(() => readStoredPosLayoutMode());

	const { api, session, access } = useAuth();
	const { setLoadingMessage, queueReRun, activeDepot, t } = useUI();
	const {
		pendingTasks,
		setPendingTasks,
		fetchedTask,
		setFetchedTask,
		activeTask,
		addNewTask,
		setItems,
		setBarcodes,
		setItemsSourceId,
		clientSituation,
		agentSituation,
		isCreditLoading,
		isAgentCreditLoading,
		apiRef,
		items,
		useClientCredit,
		useAgentCredit,
		shortCircuit,
	} = useSale();
	const { createdAt, updatedAt, active, items: itms, ...bundleDetails } = activeTask || {};
	const { appSettings } = useAppSocket();

	const mountRunRef = useRef<boolean>(true);
	const prevTaskIdRef = useRef<string | null>(null);
	const validateBtnRef = useRef<HTMLButtonElement>(null);
	const savedTaskIdRef = useRef<string>("");
	const otherTaskRef = useRef<SaleTask<Item>[]>([]);

	const navigate = useNavigate();
	const location = useLocation();

	const bundle = location.state?.payload;
	const offerLoad = location.state?.offer as { id?: string; posId?: number } | undefined;

	// An existing document's draft is scratch work: it must not survive the
	// modal, saved or not, nor reach storage with the real drafts.
	useEphemeralEditTask({
		editing: Boolean(bundle || offerLoad),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
	});

	const reviveSaleTask = (task: any): SaleTask<Item> =>
		refreshEmptyActiveTaskDate({
			...task,
			createdAt: new Date(task.createdAt),
			updatedAt: new Date(task.updatedAt),
			items: revivePendingItems(task.items, (item: any) => new Item(item)),
			contact: task.contact ?? null,
			is_public: Boolean(task.is_public),
			server_v: Number(task.server_v || 0),
			total: function () {
				return (this.items ?? []).reduce((sum: number, item: { total: number }) => addMoney(sum, item.total), 0);
			},
		});

	const { userId, removeRemote, setPublic } = usePendingWorkSessions({
		workType: "sale",
		enabled: Boolean(!bundle && !offerLoad),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
		reviveTask: reviveSaleTask,
		storageKey: LocalStorageKeys.SALE_TASKS,
		extraTasksRef: otherTaskRef,
		belongsHere: (task) => task.source === activeDepot,
	});

	const actions = [
		...((bundle || offerLoad)
			? [{
				icon: <ArrowBack />,
				name: t("common:ui.buttons.cancel"),
				onclick: () => navigate(location.state?.src, { state: { changes: false } }),
			}]
			: []),
		...(!bundle && !offerLoad
			? [{
				icon: <PowerOff />,
				name: t("common:pos.disconnect"),
				onclick: () => setConfirmOpen(true),
			}]
			: []),
		...(!bundle && !offerLoad
			? [{
				icon:
					<Badge badgeContent={pendingTasks.length} color="primary">
						<PendingActionsIcon color="action" />
					</Badge>,
				name: t("common:pos.tasks"),
				onclick: () => toggleDrawer(true)(),
			}]
			: []),
	];

	const totalEnteredAmount = Object.values(activeTask?.payment || {})
		.filter(payment => payment.enabled && Number.isFinite(payment.amount) && payment.amount > 0)
		.reduce((sum, payment) => addMoney(sum, payment.amount), 0);

	const bundleItems = itms
		? itms.map(({ stock, ...rest }) => rest)
		: [];

	const paymentsInitialized = Object.keys(activeTask?.payment || {}).length > 0;

	const canValidate = (): boolean => {
		if (!activeTask) return false;
		if (activeTask.items!.length < 1 || !validateDetail || isCreditLoading || isAgentCreditLoading) return false;
		if (compareMoney(activeTask.total(), totalEnteredAmount) < 0 || !activeTask.date) return false;
		if (bundle && taskHash === Util.hashObject(JSON.stringify(bundleDetails) + JSON.stringify(bundleItems))) return false;

		if (activeTask.receiver === 1) {
			return compareMoney(activeTask.total(), totalEnteredAmount) === 0;
		} else {
			const sc = shortCircuit();
			if (sc.shouldBypass) {
				return true;
			} else {
				if (sc.type === "client") {
					if (!clientSituation || activeTask.receiver !== clientSituation.id) return false;
					return compareMoney(Math.max(0, clientSituation.credit_limit - (clientSituation.after_total_balance ?? 0)), activeTask.total() - totalEnteredAmount) >= 0;
				} else if (sc.type === "agent") {
					if (!agentSituation || activeTask.agent !== agentSituation?.id) return false;
					return compareMoney(Math.max(0, (agentSituation?.credit_limit ?? 0) - (agentSituation?.after_total_balance ?? 0)), activeTask.total() - totalEnteredAmount) >= 0;
				} else if (sc.type === "both") {
					if (!clientSituation || activeTask.receiver !== clientSituation.id) return false;
					if (!agentSituation || activeTask.agent !== agentSituation?.id) return false;
					return compareMoney(
						Math.max(0, clientSituation.credit_limit - (clientSituation.after_total_balance ?? 0))
						+ Math.max(0, (agentSituation?.credit_limit ?? 0) - (agentSituation?.after_total_balance ?? 0)),
						activeTask.total() - totalEnteredAmount,
					) >= 0;
				}
			}
		}
		return false;
	}

	const fetchData = async () => {
		setLoadingMessage("FETCH");
		setIsDataLoading(true);

		const res = await api.get(`/records/${bundle.batch}`);

		if (res) {
			if (res.status === 200) {
				res.data.records.sort((a: any, b: any) => a.item_name.localeCompare(b.item_name));
				setFetchedTask(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsDataLoading(false);
		setLoadingMessage(null);
	};

	const fetchOffer = async () => {
		if (!offerLoad?.id) return;
		setLoadingMessage("FETCH");
		setIsDataLoading(true);
		const res = await api.get(`/offers/${offerLoad.id}`);
		if (res?.status === 200) {
			// Left in the document's own order (the API returns them by sort_order):
			// the invoice has to read like the quote the customer was given, and one
			// item may appear on several lines at different prices.
			const records = Array.isArray(res.data.records) ? res.data.records : [];
			setFetchedTask({
				...res.data,
				records,
				offerPosId: offerLoad.posId,
			});
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchOffer);
		}
		setIsDataLoading(false);
		setLoadingMessage(null);
	};

	const onSubmit = async () => {
		setConfirmModalOpen(true);
	};

	const toggleDrawer = (newOpen: boolean) => () => {
		setTasksOpen(newOpen);
	};

	const handleConfirm = async () => {
		if (!activeTask) return;

		if (!Util.checkDating(activeTask.date, access, 'sale')) {
			return;
		}

		const batch = bundle ? activeTask.id : v4();

		savedTaskIdRef.current = batch;

		const details = {
			batch: batch,
			receiver: activeTask.receiver,
			agent: activeTask.agent,
			rec_date: activeTask.date,
			ref: activeTask.reference,
			description: activeTask.description,
			source_depot: activeTask.source
		};

		const taskItems = flushItemTableItems<Item>(apiRef) ?? activeTask.items!;
		const records = taskItems.map(({ id, item, price, quantity }) => ({ item: id, item_desc: item, amount: price, quantity, batch: batch }));

		const payments = expandPaymentEntries(
			Object.entries(activeTask.payment),
			{
				invoice: batch,
				pay_date: activeTask.date,
				is_instant: true,
				description: activeTask.description,
			},
			{ includeChange: Boolean(activeTask.issueChange) },
		);

		const contactPayload = contactToPayload(activeTask.contact);

		const payload = {
			details: details,
			records: records,
			...(payments.length > 0 && { payments: payments }),
			...(activeTask.offerId ? { offer: activeTask.offerId } : {}),
			...(contactPayload ? { contact: contactPayload } : {}),
		}

		setLoadingMessage(bundle ? "UPDATE" : "SAVE");

		try {
			const res = !bundle ? await api.post("/sales", payload) : await api.put(`/sales/${batch}`, payload);

			if (res) {
				if (res.status === 201 || res.status === 204) {
					if (!bundle) {
						if (offerLoad && location.state?.src) {
							toast.success(t("common:toast.success.sale"), { autoClose: 3000 });
							navigate(location.state.src, { state: { changes: true } });
							return;
						}
						if (canPrintReceipt(access) && Util.compareStrings((appSettings?.UI_PRINT_RECEIPT.value || "NEVER"), "ALWAYS")) {
							await printReceipt(savedTaskIdRef.current);
						} else if (canPrintReceipt(access) && Util.compareStrings((appSettings?.UI_PRINT_RECEIPT.value || "NEVER"), "PROMPT")) {
							setPrintConfirmOpen(true);
						}
					} else {
						if (canPrintReceipt(access) && !Util.compareStrings((appSettings?.UI_PRINT_RECEIPT.value || "ALWAYS"), "NEVER")) {
							setPrintConfirmOpen(true);
						} else {
							navigate(location.state?.src, { state: { changes: true } });
						}
						return;
					}
					if (bundle && location.state?.src) {
						navigate(location.state?.src, { state: { changes: true } });
					} else {
						// Clear catalog cache then re-fetch so next task has up-to-date stock.
						// (Layout-switch cache only keys off depot; source is unchanged after sale.)
						const depotId = activeTask.source;
						setItems([]);
						setBarcodes({});
						setItemsSourceId(null);
						toast.success(t("common:toast.success.sale"), { autoClose: 3000 });
						deleteTask(activeTask.id, { posted: true }); // Posted: clear it for everyone, not just its owner
						addNewTask();
						if (depotId > 0) {
							const reloadCatalog = async () => {
								try {
									const catalogRes = await api.get(`items/${depotId}/sales`);
									if (catalogRes?.status === 200) {
										setItems(catalogRes.data.items || []);
										setBarcodes(catalogRes.data.barcodes || {});
										setItemsSourceId(depotId);
									} else if (catalogRes?.status === 401 || catalogRes?.status === 422) {
										queueReRun(reloadCatalog);
									}
								} catch {
									// Leave empty; user can hit refresh on the catalog
								}
							};
							void reloadCatalog();
						}
					}
				} else {
					toast.error(t("common:toast.error.pos.unknown"), { autoClose: 3000 });
					console.error("Failed to send data:", res);
				}
			}
		} finally {
			setLoadingMessage(null);
		}
	}

	const deleteTask = (taskId: string, { posted = false }: { posted?: boolean } = {}) => {
		const existing = pendingTasks.find((task) => task.id === taskId);
		setPendingTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
		void removeRemote(taskId, existing?.server_v, { posted });
	};

	useEffect(() => {
		if (bundle || offerLoad) {
			addNewTask();
		} else {
			const parsedTasks = loadPendingTasks(LocalStorageKeys.SALE_TASKS, reviveSaleTask, session?.user_id);
			if (parsedTasks) {
				// first filter to get only tasks for the active POS
				setPendingTasks(parsedTasks.filter((task: any) => task.source === activeDepot));
				otherTaskRef.current = parsedTasks.filter((task: any) => task.source !== activeDepot);
				return;
			}
			addNewTask();
		}
	}, []);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// Ctrl+Alt+Q — task pane (avoid bare Ctrl+Q: quits many browsers / sits next to W)
			if (!bundle && !offerLoad && e.ctrlKey && e.altKey && e.key.toLowerCase() === "q") {
				e.preventDefault();
				setTasksOpen((open) => !open);
			} else if (e.ctrlKey && e.altKey && e.key.toLowerCase() === "s") {
				e.preventDefault();
				validateBtnRef.current?.click();
			}
		};

		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [bundle]);

	useEffect(() => {
		if (!bundle && !offerLoad && activeDepot < 1) {
			toast.error(t("common:toast.error.pos.noActive"), { autoClose: 3000 });
			setDisconnecting(true);
		}
	}, []);

	// Top bar disconnect button (outside SaleProvider tree)
	useEffect(() => {
		if (bundle || offerLoad) return;
		const onTopBarDisconnect = () => setConfirmOpen(true);
		window.addEventListener(POS_DISCONNECT_EVENT, onTopBarDisconnect);
		return () => window.removeEventListener(POS_DISCONNECT_EVENT, onTopBarDisconnect);
	}, [bundle]);

	// Top bar layout toggle (Classic | Visual | Split)
	useEffect(() => {
		const onLayoutChange = (ev: Event) => {
			const mode = (ev as CustomEvent<PosLayoutMode>).detail;
			if (mode === "classic" || mode === "visual" || mode === "split") {
				setLayoutMode(mode);
				writeStoredPosLayoutMode(mode);
			}
		};
		window.addEventListener(POS_LAYOUT_CHANGE_EVENT, onLayoutChange);
		return () => window.removeEventListener(POS_LAYOUT_CHANGE_EVENT, onLayoutChange);
	}, []);

	// useEffect(() => {
	// 		console.log("Validate Detail", validateDetail)
	// 	console.log("Validate lodating", !isCreditLoading && !isAgentCreditLoading)
	// 	console.log("Validate Receiver", activeTask?.receiver === clientSituation?.id)
	// 	console.log("Validate Agent", (!activeTask?.agent || activeTask.agent === (agentSituation?.id ?? -1)), activeTask?.agent, agentSituation?.id)
	// 	console.log("Another Banger", (useClientCredit || ((!useClientCredit && !useAgentCredit) && activeTask?.agent === activeTask?.receiver)) )
	// 	console.log("Last check", clientSituation?.credit_limit < 0, Math.max(0, clientSituation?.credit_limit - (clientSituation?.total_balance ?? 0)) >= (activeTask?.total() ?? 0) - totalEnteredAmount)
	// 	console.log("Last check 2", clientSituation?.credit_limit, Math.max(0, clientSituation?.credit_limit - (clientSituation?.total_balance ?? 0)), (activeTask?.total() ?? 0) - totalEnteredAmount)
	// }, [activeTask]);

	useEffect(() => {
		if (mountRunRef.current) {
			mountRunRef.current = false;
			return;
		}

		// Always persist pending task when not in edit mode (i.e., when bundle is not provided)
		if (!bundle && !offerLoad) {
			if (pendingTasks.length === 0) {
				addNewTask();
				return;
			}
			const allTasks = [...pendingTasks, ...otherTaskRef.current];
			persistPendingTasks(LocalStorageKeys.SALE_TASKS, session?.user_id, JSON.stringify(allTasks));
		}

		// If there are pending task and no active task, set the first task as active
		// This ensures that there is always an active task to work with
		// Empty tasks get today's date when activated if their date is stale
		if (pendingTasks.length > 0 && !activeTask) {
			setPendingTasks((prevTasks) =>
				mapTasksWithActivation(prevTasks, (_task, index) => index === 0)
			);
		}
	}, [pendingTasks]);

	useEffect(() => {
		if (activeTask) {
			if (!renderChildren) setRenderChildren(true);

			// Lazy fetch data if in edit mode and only one pending task exists
			// This is to ensure that the data is fetched only when there is a single task to edit
			// This is because calling it immediately after addNewTask might fetch when there are no tasks yet
			// because setting state is asynchronous
			if (prevTaskIdRef.current !== activeTask.id) {
				if (bundle && !isDataLoading) {
					fetchData();
				} else if (offerLoad && !isDataLoading) {
					fetchOffer();
				}
				prevTaskIdRef.current = activeTask.id;
				// setTasksOpen(false);
			}

			// If in edit mode, calculate the task hash
			// This is used to determine if the task details have changed
			// and if the update can proceed
			if (bundle && activeTask && bundleItems.length > 0 && paymentsInitialized && !taskHash) {
				setTaskHash(
					Util.hashObject(
						JSON.stringify(bundleDetails)
						+
						JSON.stringify(bundleItems)
					)
				);
			}
		} else {
			prevTaskIdRef.current = null;
		}
	}, [activeTask]);

	useEffect(() => {
		return setupQzTray();
	}, []);

	const printReceipt = async (invoiceId: string) => {
		await printReceiptPdf(api, invoiceId, setLoadingMessage);
		navigate(location.state?.src, { state: { changes: true } });
	};

	const onDeleteClicked = (uniqueId: string) => {
		updateActiveTask(setPendingTasks, prev => ({
			items: prev.items
				? prev.items.filter(item => item.uniqueId !== uniqueId)
				: [],
		}));
	};

	const allowPosAliasing = Util.isBoolSettingEnabled(appSettings?.ALLOW_POS_ALIASING?.value);

	// All layouts hide the ItemTable footer and may use list cart (unmounts ItemTable).
	// Publish count + currency total to the app bottom bar from the active cart.
	const statusCartLines = activeTask?.items ?? [];
	const statusCartTotal = useMemo(
		() => statusCartLines.reduce((sum, row) => addMoney(sum, (Number(row.total) || 0)), 0),
		[statusCartLines],
	);
	useActiveTaskStatusTotal(
		statusCartTotal,
		statusCartLines.length,
		Boolean(activeTask),
	);

	const columns: GridColDef<Item>[] = [
		{
			field: 'rowNumber',
			headerName: '#',
			width: 20,
			valueGetter: (_, row) => apiRef.current!.getRowIndexRelativeToVisibleRows(row.uniqueId) + 1,
		},
		...(Util.isBoolSettingEnabled(appSettings?.UI_SHOW_ITEM_CODE?.value)
			? [
				{
					field: 'item_code',
					headerName: t('common:tables.itemCode'),
				},
			]
			: []),
		{
			field: 'item',
			headerName: t('common:tables.item'),
			flex: 2,
			minWidth: 250,
			editable: allowPosAliasing,
			...(allowPosAliasing
				? {
					renderEditCell: (params) => (
						<PosAliasEditCell
							{...params}
							catalogItems={items}
						/>
					),
				}
				: {}),
		},
		...(Util.isBoolSettingEnabled(appSettings?.UI_SHOW_CATEGORY?.value)
			? [
				{
					field: 'category',
					headerName: t('common:tables.category'),
				},
			]
			: []),
		{
			field: 'quantity',
			headerName: t('common:tables.quantity'),
			type: 'number',
			editable: canChangeLineQty(access)
		},
		{
			field: 'price',
			headerName: t('common:tables.amount'),
			type: 'number',
			editable: canEditSalePrice(access)
		},
		{
			field: 'total',
			headerName: t('common:tables.lineTotal'),
			flex: 1,
			minWidth: 150,
			maxWidth: 150,
			type: 'number',
			description: t('common:tables.total'),
		},
		{
			field: 'actions',
			headerName: '',
			sortable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams<Item>) => (
				canRemoveLine(access)
					? <Tooltip title={t("common:ui.tooltips.delete")}>
						<IconButton color="error" onClick={() => { onDeleteClicked(params.row.uniqueId) }}>
							<RemoveCircleOutline />
						</IconButton>
					</Tooltip>
					: null
			),
		},
	];

	const getMinSellPrice = canSellBelowMinPrice(access)
		? undefined
		: (row: Item) => catalogMinSellPrice(items.find((c: any) => c.id === row.id), row);

	const layoutProps = {
		renderChildren,
		setValidateDetail,
		bundle: fetchedTask?.records,
		columns,
		allowPosAliasing,
		totalEnteredAmount,
		apiRef,
		setPendingTasks,
		getMinSellPrice,
	};

	return (
		<Box
			sx={{
				height: "100%",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				position: "relative",
				p: 1,
				boxSizing: "border-box",
			}}
		>
			<Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
				{layoutMode === "classic" && <PosClassicLayout {...layoutProps} />}
				{layoutMode === "visual" && <PosVisualLayout {...layoutProps} />}
				{layoutMode === "split" && <PosSplitLayout {...layoutProps} />}
			</Box>

			<Box>
				<TaskPane<Item, SaleTask<Item>>
					tasks={pendingTasks}
					setTasks={setPendingTasks}
					addNewTask={addNewTask}
					tasksOpen={tasksOpen}
					onCallback={() => setTasksOpen(false)}
					currentUserId={userId}
					onTogglePublic={(task) => { void setPublic(task, !task.is_public); }}
					onDeleteTask={(id: string) => deleteTask(id)}
				/>
			</Box>

			<Fab
				variant="extended"
				aria-label={t("common:pos.pendingTasksAria")}
				sx={{
					position: 'fixed',
					// Clear the app bottom bar (~footer-height) + speed dial stack
					bottom: 'calc(var(--footer-height) + 72px)',
					right: 50,
					zIndex: Z_INDEX.fab,
					color: "#fdfdfdff",
					backgroundColor: "#109872ff",
				}}
				disabled={!canValidate()}
				onClick={onSubmit}
				ref={validateBtnRef}
			>
				<CheckIcon sx={{ mr: 1 }} />
				{bundle ? t("common:pos.update") : t("common:pos.validate")}
			</Fab>

			<SpeedDial
				color="action"
				ariaLabel={t("common:pos.speedDialAria")}
				sx={{
					position: 'fixed',
					bottom: 'calc(var(--footer-height) + 12px)',
					right: 50,
					zIndex: Z_INDEX.fab,
				}}
				icon={<SpeedDialIcon openIcon={<Close />} />}
				open={sdOpen}
				onClose={() => setSDOpen(false)}
				onOpen={() => setSDOpen(true)}
			>
				{actions.map((action) => (
					<SpeedDialAction
						key={action.name}
						icon={action.icon}
						slotProps={{
							tooltip: {
								title: action.name,
							},
						}}
						onClick={action.onclick}
					/>
				))}
			</SpeedDial>

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				<ConfirmDialog
					onConfirm={handleConfirm}
					isOpen={confirmModalOpen}
					setIsOpen={setConfirmModalOpen}
					title={t("common:dialog.salesConfirmation")}
					confirmBtnColor="success" />
			}

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				<ConfirmDialog
					onConfirm={() => {
						if (document.fullscreenElement) {
							void document.exitFullscreen().catch(() => undefined);
						}
						setDisconnecting(true);
					}}
					isOpen={confirmOpen}
					setIsOpen={setConfirmOpen}
					title={t("common:dialog.disconnectConfirmation")}
					confirmBtnColor="error"
					confirmBtnIcon={<PowerOff />}
					confirmBtnText={t("common:pos.disconnect")}
					confirmBtnVariant="outlined"
				/>
			}

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				<ConfirmDialog
					onConfirm={() => printReceipt(savedTaskIdRef.current)}
					isOpen={printConfirmOpen}
					setIsOpen={setPrintConfirmOpen}
					title={t("common:dialog.printConfirmation")}
					confirmBtnColor="primary"
					confirmBtnIcon={<Print />}
					confirmBtnText={t("common:ui.buttons.print")}
					confirmBtnVariant="outlined"
					message={
						<>{t("common:dialog.printReceiptQuestion")}</>
					}
					onCancel={() => {
						if (bundle) {
							navigate(location.state?.src, { state: { changes: true } });
						}
					}}
				/>
			}



			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				disconnecting &&
				<ShutdownScreen onFinish={() => navigate("/pos/terminals")} pos={activeDepot} />
			}

		</Box>
	);
};

export default PointOfSale;
